import { useMutation, useQuery } from '@apollo/client';
import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  FormControl,
  Grid,
  IconButton,
  InputBase,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Typography,
} from '@mui/material';
import { useNavigate } from '@tanstack/react-router';
import { type FormikHelpers, useFormik } from 'formik';
import React, { type ChangeEvent, useEffect, useRef, useState } from 'react';
import { Card } from 'src/Administration/Common';
import allFieldDefinitionsQuery from 'src/api/admin/allFieldDefinitionsQuery';
import allFieldMapsConfigQuery from 'src/api/admin/allFieldMapsConfigQuery';
import createFieldMapsConfigMutation from 'src/api/admin/createFieldMapsConfigMutation';
import createFieldMapsMutation from 'src/api/admin/createFieldMapsMutation';
import { useCustomToast } from 'src/hooks/menu';
import * as Yup from 'yup';
import type { AllFieldDefsData, FieldDefinition } from '../../types';
import styles from './AddFieldMapsPageStyles';

interface FieldOption {
  externalIdValue: string;
  fieldDefValue: string;
}

interface AddFieldMapsFormValues {
  name: string;
  externalFieldId: string;
  fieldDefId: string;
}

interface CreateFieldMapConfigResponse {
  createFieldMapConfig: {
    fieldMapConfig: {
      id: string;
    };
  };
}

const AddFieldMapsPage: React.FC = () => {
  const { toastr } = useCustomToast();
  const GRID_CONTAINER_SPACING = 4;
  const classes = styles();
  const navigate = useNavigate();
  const nameInputRef = useRef<HTMLInputElement>(null);

  const onClickCancel = (): void => {
    navigate({ to: '/systemadmin/metadata/field-maps' });
  };

  const [fieldOptions, setFieldOptions] = useState<FieldOption[]>([{ externalIdValue: '', fieldDefValue: '' }]);

  const { data: allAvailableFieldDefs } = useQuery<AllFieldDefsData>(allFieldDefinitionsQuery, {
    fetchPolicy: 'cache-and-network',
  });
  const allAvailableFieldDefsNode: FieldDefinition[] | undefined = allAvailableFieldDefs?.allFieldDefs?.edges?.map(
    (item) => item.node,
  );

  const [createFieldMapsConfig] = useMutation<CreateFieldMapConfigResponse>(createFieldMapsConfigMutation, {
    fetchPolicy: 'network-only',
    refetchQueries: [allFieldMapsConfigQuery],
  });

  const [createFieldMaps] = useMutation(createFieldMapsMutation, {
    fetchPolicy: 'network-only',
    refetchQueries: [allFieldMapsConfigQuery],
  });

  const addOptionHandler = (): void => {
    const emptyField: FieldOption = { externalIdValue: '', fieldDefValue: '' };
    setFieldOptions(() => [...fieldOptions, emptyField]);
  };

  const removeOptionHandler = (index: number): void => {
    setFieldOptions((prevState) => {
      const updatedPairs = [...prevState];
      updatedPairs.splice(index, 1);
      return updatedPairs;
    });
  };

  const updateFieldOptions = (index: number, keyOrValue: keyof FieldOption, newValue: string): void => {
    setFieldOptions((prevState) => {
      const updatedPairs = [...prevState];
      updatedPairs[index] = { ...updatedPairs[index], [keyOrValue]: newValue };
      return updatedPairs;
    });
  };

  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);

  const formik = useFormik<AddFieldMapsFormValues>({
    initialValues: {
      name: '',
      externalFieldId: '',
      fieldDefId: '',
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Please provide a field name'),
    }),
    onSubmit: async (values, submitProps: FormikHelpers<AddFieldMapsFormValues>) => {
      const { name } = values;

      createFieldMapsConfig({
        variables: {
          name,
        },
      })
        .then((res) => {
          const configId = res.data?.createFieldMapConfig.fieldMapConfig.id;
          if (configId) {
            for (const { externalIdValue, fieldDefValue } of fieldOptions) {
              createFieldMaps({
                variables: {
                  fieldMapConfigId: configId,
                  externalFieldId: externalIdValue,
                  fieldDefId: fieldDefValue,
                },
              });
            }
          }
        })
        .then(() => {
          setFieldOptions([{ externalIdValue: '', fieldDefValue: '' }]);
          toastr.success(`Field '${name}' was created successfully`);
          submitProps.resetForm({ values });
          setHasSubmitted(true);
        })
        .catch((e: Error) => {
          toastr.error(e.message);
        });
    },
  });

  useEffect(() => {
    if (!formik.dirty && hasSubmitted) {
      navigate({ to: '/systemadmin/metadata/field-maps' });
    }
  }, [formik.dirty, hasSubmitted, navigate]);

  useEffect(() => {
    if (nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, []);

  return (
    <form onSubmit={formik.handleSubmit}>
      <Card className={classes.mainCard}>
        <Grid className={classes.mainGrid} container spacing={GRID_CONTAINER_SPACING}>
          <div className={classes.textWrapper}>
            <Typography className={classes.newFieldText}>Add New Field</Typography>
            <Typography className={classes.fieldErrors}>{formik.errors.name}</Typography>
          </div>
          <Grid style={{ justifyContent: 'flex-end' }} className={classes.displayName} item xs={12}>
            <Typography className={classes.displayNameText}>Name</Typography>
            <InputBase {...formik.getFieldProps('name')} className={classes.displayNameInput} inputRef={nameInputRef} />
            <div className={classes.externalDefText}>
              <Typography className={classes.externalFieldIdText}>External Field ID</Typography>
              <Typography className={classes.fieldDefinitionText}>Field Definition</Typography>
            </div>
          </Grid>

          <Grid className={classes.choicesSection} item xs={12}>
            {fieldOptions?.map((pair, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: field options have no stable identifier until saved
              <div className={classes.choicesInputs} key={`field-option-${index}`}>
                <InputBase
                  value={pair.externalIdValue}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    updateFieldOptions(index, 'externalIdValue', e.target.value)
                  }
                  className={classes.optionInput}
                />
                <FormControl className={classes.fieldTypeSelect}>
                  <Select
                    value={pair.fieldDefValue}
                    onChange={(e: SelectChangeEvent<string>) =>
                      updateFieldOptions(index, 'fieldDefValue', e.target.value)
                    }
                    placeholder="Select Role"
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    label="Select Role"
                  >
                    {allAvailableFieldDefsNode?.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <IconButton onClick={() => removeOptionHandler(index)} size="large">
                  <CloseIcon />
                </IconButton>
              </div>
            ))}

            <div className={classes.addOptionButton}>
              <Button onClick={addOptionHandler} variant="contained" color="primary">
                Add
              </Button>
            </div>
          </Grid>

          <Grid className={classes.addCancelButtons} item xs={12}>
            <Button disabled={Object.keys(formik.errors).length > 0} variant="contained" color="primary" type="submit">
              Save
            </Button>
            <Button
              color="secondary"
              style={{
                marginLeft: '10px',
                marginRight: '10px',
              }}
              onClick={onClickCancel}
            >
              Cancel
            </Button>
          </Grid>
        </Grid>
      </Card>
    </form>
  );
};

export default AddFieldMapsPage;
